import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { UsersService } from '@webapp/users/services/users.service';
import { extractErrorMessage } from '@webapp/auth/utils/extract-error-message';
import { ButtonComponent, IconComponent, InputErrorComponent } from '@webapp/ui';

@Component({
  selector: 'activar-referral-code-banner',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent, ButtonComponent, InputErrorComponent],
  templateUrl: './referral-code-banner.component.html',
  styleUrl: './referral-code-banner.component.scss',
})
export class ReferralCodeBannerComponent {
  // ── Public API ──────────────────────────────────────────────────────────────

  /** ID of the quote this referral code will be associated to. */
  readonly quoteId = input.required<string>();

  /** Emits the applied code on success, or `null` on reset / invalid. */
  readonly codeApplied = output<string | null>();

  /** Emits `true` when validation starts, `false` when it resolves. */
  readonly validating = output<boolean>();

  // ── Internal state ──────────────────────────────────────────────────────────

  private readonly usersService = inject(UsersService);

  protected readonly referralCodeControl = new FormControl<string>('', { nonNullable: true });

  protected readonly viewState = signal<{ kind: 'prompt' } | { kind: 'success'; code: string }>(
    { kind: 'prompt' }
  );

  protected readonly isValidating = signal(false);
  protected readonly validationError = signal<string | null>(null);

  /** Typed helper for template narrowing of success state. */
  protected readonly successCode = computed(() =>
    this.viewState().kind === 'success' ? (this.viewState() as { kind: 'success'; code: string }).code : ''
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  protected async apply(): Promise<void> {
    const raw = this.referralCodeControl.value.trim();

    if (!raw) {
      this.validationError.set('Ingresá un código.');
      return;
    }

    this.isValidating.set(true);
    this.validating.emit(true);
    this.validationError.set(null);

    try {
      const response = await firstValueFrom(
        this.usersService.validateReferralCode(raw, this.quoteId())
      );

      if (response?.valid === true) {
        this.viewState.set({ kind: 'success', code: raw });
        this.codeApplied.emit(raw);
      } else {
        this.referralCodeControl.setErrors({ referralCodeInvalid: true });
        this.validationError.set(response?.message ?? 'El código ingresado no es válido.');
        this.codeApplied.emit(null);
      }
    } catch (error) {
      this.referralCodeControl.setErrors({ referralCodeInvalid: true });
      this.validationError.set(
        extractErrorMessage(error, 'No se pudo validar el código. Intente nuevamente.')
      );
      this.codeApplied.emit(null);
    } finally {
      this.isValidating.set(false);
      this.validating.emit(false);
    }
  }

  protected onInput(): void {
    this.validationError.set(null);
    this.referralCodeControl.setErrors(null);
    this.codeApplied.emit(null);
  }

  protected reset(): void {
    this.referralCodeControl.setValue('');
    this.referralCodeControl.setErrors(null);
    this.validationError.set(null);
    this.viewState.set({ kind: 'prompt' });
    this.codeApplied.emit(null);
  }
}
